/**
 * Backend CRUD controllers return `ApiResponse<T>` with snake_case fields.
 * Some endpoints return raw bodies (Spring Page, cart, etc.) — skip unwrapping for those paths.
 */

/** @param {string} [endpoint] */
export function shouldBypassApiResponseUnwrap(endpoint) {
  if (!endpoint || typeof endpoint !== "string") return false;
  if (endpoint.includes("/product/filter")) return true;
  if (endpoint.includes("/product/names")) return true;
  return false;
}

function isApiResponseEnvelope(body) {
  return (
    body !== null &&
    typeof body === "object" &&
    "success" in body &&
    ("data" in body || "error" in body)
  );
}

function throwApiResponseError(envelope) {
  const err = envelope.error;
  const msg =
    (err && typeof err === "object" && (err.message || err.code)) ||
    "Request failed";
  const e = new Error(String(msg));
  e.apiError = err;
  throw e;
}

/**
 * @param {unknown} body - Parsed JSON response body
 * @param {{ endpoint?: string }} [options]
 * @returns {unknown} Unwrapped `data` or original body
 */
export function unwrapMarketplaceBody(body, options = {}) {
  const { endpoint } = options;
  if (body == null) return body;
  if (!isApiResponseEnvelope(body)) {
    return body;
  }
  const envelope = body;
  if (envelope.success === false) {
    throwApiResponseError(envelope);
  }
  if (shouldBypassApiResponseUnwrap(endpoint) && envelope.data === undefined) {
    return body;
  }
  return envelope.data;
}

/**
 * Unwrap list endpoints that return `data` as an array plus optional `pagination`.
 * @param {unknown} body
 * @param {{ endpoint?: string }} [options]
 * @returns {{ items: unknown[], pagination: Record<string, unknown>|null }}
 */
export function unwrapMarketplaceListBody(body, options = {}) {
  if (body == null) {
    return { items: [], pagination: null };
  }
  if (!isApiResponseEnvelope(body)) {
    return {
      items: normalizeCollectionResponse(body),
      pagination: null,
    };
  }
  if (body.success === false) {
    throwApiResponseError(body);
  }
  return {
    items: normalizeCollectionResponse(body.data),
    pagination:
      body.pagination && typeof body.pagination === "object"
        ? body.pagination
        : null,
  };
}

/**
 * Normalize list/collection payloads after unwrap (supports legacy HATEOAS and Spring Page).
 * @param {unknown} data
 * @returns {unknown[]}
 */
export function normalizeCollectionResponse(data) {
  if (data == null) return [];
  if (Array.isArray(data)) return data;
  if (typeof data === "object" && Array.isArray(data.content))
    return data.content;
  if (typeof data === "object" && data._embedded) {
    const embedded = data._embedded;
    for (const key of Object.keys(embedded)) {
      const v = embedded[key];
      if (Array.isArray(v)) return v;
    }
  }
  return [];
}

/**
 * Single resource: plain object, first of an array, or first item in Page / _embedded.
 * @param {unknown} data
 * @returns {Record<string, unknown>|null}
 */
export function normalizeSingleResource(data) {
  if (data == null) return null;
  if (typeof data !== "object") return null;
  if (Array.isArray(data)) {
    return /** @type {Record<string, unknown>} */ (data[0] ?? null);
  }
  const list = normalizeCollectionResponse(data);
  if (list.length > 0) return /** @type {Record<string, unknown>} */ (list[0]);
  if ("id" in data) return /** @type {Record<string, unknown>} */ (data);
  return null;
}
